import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import { useApp } from "@/context/AppContext";
import { CATEGORIES } from "@/lib/mockData";
import { ArrowLeft, ArrowRight, Check, Briefcase, GraduationCap, Home, Heart, Sprout, Palette, Microscope, HeartHandshake, Shield, Users, Sparkles, KeyRound, Mic } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import VoiceInput from "@/components/common/VoiceInput";

const ICON_MAP = { Briefcase, GraduationCap, Home, Heart, Sprout, Palette, Microscope, HeartHandshake, Shield, Users, KeyRound };

const STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

const FLAGS = [
    { key: "veteran",        profileKey: "is_veteran",              label: "Veteran or active service",          icon: Shield },
    { key: "student",        profileKey: "currently_enrolled",      label: "Student (enrolled or planning)",    icon: GraduationCap },
    { key: "disabled",       profileKey: "is_disabled",             label: "Have a disability",                  icon: Heart },
    { key: "minority",       profileKey: "is_minority",             label: "Minority-owned / BIPOC entrepreneur", icon: Users },
    { key: "womanOwned",     profileKey: "is_woman_owned",          label: "Woman-owned business (50%+)",        icon: Sparkles },
    { key: "firstTimeBuyer", profileKey: "is_first_time_homebuyer", label: "First-time homebuyer",               icon: Home },
    { key: "entrepreneur",   profileKey: "has_business",            label: "Small business owner / founder",    icon: Briefcase },
    { key: "reentry",        profileKey: "is_formerly_incarcerated", label: "Recently released / reentry",      icon: KeyRound },
    { key: "parent",         profileKey: "is_parent",               label: "Parent / guardian",                  icon: HeartHandshake },
];

const GOALS = [
    { id: "start", label: "Start a business" },
    { id: "grow", label: "Grow an existing business" },
    { id: "school", label: "Pay for school" },
    { id: "home", label: "Buy or improve a home" },
    { id: "recover", label: "Recover money I'm owed" },
    { id: "research", label: "Fund research or creative work" },
    { id: "community", label: "Serve my community" },
];

export default function Onboarding() {
    const navigate = useNavigate();
    const { profile, updateProfile, toggleFlag, toggleCategory, toggleGoal, refreshUnclaimed } = useApp();
    const [step, setStep] = useState(0);
    const [saving, setSaving] = useState(false);
    const totalSteps = 5;

    // Kick off the unclaimed-property scan in the BACKGROUND the moment the user
    // selects a state. By the time they finish onboarding, the scan is already
    // populating AppContext, so the dashboard paints with real data instantly.
    const scannedStateRef = useRef(null);
    useEffect(() => {
        if (profile.state && profile.state !== scannedStateRef.current) {
            scannedStateRef.current = profile.state;
            // Fire-and-forget — do NOT await.
            refreshUnclaimed(profile.state).catch(() => { /* silent; retried on dashboard load */ });
        }
    }, [profile.state, refreshUnclaimed]);

    const next = async () => {
        if (step === 1 && !profile.state) {
            toast.error("Please select your state to personalize matches.");
            return;
        }
        if (step < totalSteps - 1) setStep(step + 1);
        else await finish();
    };
    const back = () => setStep(Math.max(0, step - 1));

    const finish = async () => {
        setSaving(true);
        try {
            await updateProfile({ completed_onboarding: true });
            // Scan already running in the background from StepBasics. We don't
            // block the navigate on it — dashboard will repaint when results arrive.
            toast.success("Scanning matched programs for you…");
            setTimeout(() => navigate("/app/dashboard"), 300);
        } catch {
            toast.error("Couldn't save your profile. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-hero relative overflow-hidden">
            <div aria-hidden className="absolute top-20 -left-40 h-[420px] w-[420px] rounded-full bg-accent/15 blur-[120px]" />
            <div aria-hidden className="absolute bottom-0 right-0 h-[380px] w-[380px] rounded-full bg-primary/10 blur-[140px]" />

            <header className="relative mx-auto max-w-5xl px-5 sm:px-8 h-16 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v18"/><path d="M17 7H9.5a3.5 3.5 0 0 0 0 7H14a3.5 3.5 0 0 1 0 7H6"/></svg>
                    </span>
                    <span className="font-serif text-xl tracking-tight">Owed</span>
                </Link>
                <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Step {step + 1} / {totalSteps}</p>
            </header>

            <div className="relative mx-auto max-w-3xl px-5 sm:px-8 pt-6 pb-20">
                <Progress value={((step + 1) / totalSteps) * 100} className="h-1 bg-secondary" />

                <Card className="mt-10 p-8 sm:p-12 border-border bg-card shadow-elegant">
                    {step === 0 && <StepIntro profile={profile} updateProfile={updateProfile} />}
                    {step === 1 && <StepBasics profile={profile} updateProfile={updateProfile} />}
                    {step === 2 && <StepFlags profile={profile} toggleFlag={toggleFlag} />}
                    {step === 3 && <StepCategories selected={profile.categories || []} toggle={toggleCategory} />}
                    {step === 4 && <StepGoals goals={profile.goals || []} toggle={toggleGoal} />}

                    <div className="mt-10 flex items-center justify-between">
                        <Button variant="ghost" onClick={back} disabled={step === 0} className="rounded-full" data-testid="onboarding-back-btn">
                            <ArrowLeft className="h-4 w-4 mr-1" /> Back
                        </Button>
                        <div className="flex items-center gap-2">
                            {step < totalSteps - 1 && (
                                <Button variant="ghost" onClick={finish} className="text-muted-foreground rounded-full" data-testid="onboarding-skip-btn">Skip for now</Button>
                            )}
                            <Button onClick={next} disabled={saving} className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-11 px-6" data-testid="onboarding-next-btn">
                                {step === totalSteps - 1 ? (saving ? "Saving…" : (<>See my matches <Sparkles className="h-4 w-4 ml-1" /></>)) : (<>Continue <ArrowRight className="h-4 w-4 ml-1" /></>)}
                            </Button>
                        </div>
                    </div>
                </Card>

                <p className="mt-8 text-center text-xs text-muted-foreground">
                    We use this only to match programs and pre-fill applications. Your data is never sold.
                </p>
            </div>
        </div>
    );
}

function StepHeader({ eyebrow, title, sub }) {
    return (
        <div>
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">{eyebrow}</p>
            <h1 className="mt-3 font-serif text-3xl sm:text-4xl leading-tight text-balance">{title}</h1>
            {sub && <p className="mt-3 text-base text-muted-foreground leading-relaxed">{sub}</p>}
        </div>
    );
}

function StepIntro({ profile, updateProfile }) {
    const cleanName = (s) => (s || "").replace(/[.?!]/g, "").trim().split(/\s+/).slice(0, 3).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
    return (
        <>
            <StepHeader eyebrow="Welcome" title="Let's get a quiet start." sub="Tell us what to call you. We'll build a tailored list of real programs in the next few screens." />
            <div className="mt-3 inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                <Mic className="h-3 w-3" /> Voice input: hold the mic next to any field
            </div>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <div>
                    <Label htmlFor="fname" className="text-sm">Legal first name</Label>
                    <div className="mt-2 flex items-center gap-2">
                        <Input id="fname" value={profile.legal_first_name || ""} onChange={(e) => updateProfile({ legal_first_name: e.target.value })} placeholder="Alex" className="h-11 rounded-xl" data-testid="onboarding-first-name-input" />
                        <VoiceInput testId="onboarding-first-name-mic" hint="Hold to speak your first name" onResult={(t) => updateProfile({ legal_first_name: cleanName(t) })} />
                    </div>
                </div>
                <div>
                    <Label htmlFor="lname" className="text-sm">Legal last name</Label>
                    <div className="mt-2 flex items-center gap-2">
                        <Input id="lname" value={profile.legal_last_name || ""} onChange={(e) => updateProfile({ legal_last_name: e.target.value })} placeholder="Morgan" className="h-11 rounded-xl" data-testid="onboarding-last-name-input" />
                        <VoiceInput testId="onboarding-last-name-mic" hint="Hold to speak your last name" onResult={(t) => updateProfile({ legal_last_name: cleanName(t) })} />
                    </div>
                </div>
            </div>
        </>
    );
}

function StepBasics({ profile, updateProfile }) {
    return (
        <>
            <StepHeader eyebrow="Basics" title="Where you live." sub="State unlocks local programs and shows your correct unclaimed-property portal." />
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
                <div>
                    <Label className="text-sm">State</Label>
                    <div className="mt-2 grid grid-cols-6 gap-1.5 max-h-48 overflow-y-auto p-1" data-testid="onboarding-state-picker">
                        {STATES.map((s) => (
                            <button key={s} onClick={() => updateProfile({ state: s })} className={cn("font-mono text-xs rounded-md py-2 border transition-colors", profile.state === s ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary")} data-testid={`state-${s}`}>
                                {s}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="space-y-4">
                    <div>
                        <Label htmlFor="income" className="text-sm">Household income (annual)</Label>
                        <Input id="income" type="number" value={profile.annual_income ?? ""} onChange={(e) => updateProfile({ annual_income: e.target.value ? Number(e.target.value) : null })} placeholder="58000" className="mt-2 h-11 rounded-xl" data-testid="onboarding-income-input" />
                    </div>
                    <div>
                        <Label htmlFor="hh" className="text-sm">Household size</Label>
                        <div className="mt-2 flex items-center gap-2">
                            <Button variant="outline" size="icon" className="rounded-full h-9 w-9" onClick={() => updateProfile({ household_size: Math.max(1, (profile.household_size || 1) - 1) })}>−</Button>
                            <span className="flex-1 text-center font-serif text-2xl" data-testid="onboarding-household-size">{profile.household_size || 1}</span>
                            <Button variant="outline" size="icon" className="rounded-full h-9 w-9" onClick={() => updateProfile({ household_size: (profile.household_size || 1) + 1 })}>+</Button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

function StepFlags({ profile, toggleFlag }) {
    return (
        <>
            <StepHeader eyebrow="About you" title="Any of these apply?" sub="Each unlocks targeted programs — select all that fit. You can always update later." />
            <div className="mt-8 grid sm:grid-cols-2 gap-3">
                {FLAGS.map((f) => {
                    const active = !!profile[f.profileKey];
                    const Icon = f.icon;
                    return (
                        <button key={f.key} onClick={() => toggleFlag(f.key)} className={cn("flex items-center gap-3 p-4 rounded-xl border text-left transition-all", active ? "border-foreground bg-foreground text-background shadow-soft" : "border-border bg-card hover:border-foreground/30")} data-testid={`onboarding-flag-${f.key}`}>
                            <span className={cn("inline-flex h-10 w-10 items-center justify-center rounded-lg", active ? "bg-background/15 text-background" : "bg-secondary text-muted-foreground")}>
                                <Icon className="h-4 w-4" />
                            </span>
                            <span className="flex-1 text-sm font-medium">{f.label}</span>
                            {active && <Check className="h-4 w-4" />}
                        </button>
                    );
                })}
            </div>
        </>
    );
}

function StepCategories({ selected, toggle }) {
    return (
        <>
            <StepHeader eyebrow="Interests" title="Which categories should we prioritize?" sub="Pick up to 4 — they bubble to the top of your match list." />
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {CATEGORIES.map((c) => {
                    const Icon = ICON_MAP[c.icon] || Sparkles;
                    const active = selected.includes(c.id);
                    return (
                        <button key={c.id} onClick={() => toggle(c.id)} className={cn("flex flex-col items-start gap-3 p-5 rounded-xl border text-left transition-all", active ? "border-foreground bg-foreground text-background shadow-soft" : "border-border bg-card hover:border-foreground/30")} data-testid={`onboarding-category-${c.id}`}>
                            <span className={cn("inline-flex h-9 w-9 items-center justify-center rounded-lg", active ? "bg-background/15" : "bg-secondary text-muted-foreground")}>
                                <Icon className="h-4 w-4" />
                            </span>
                            <span className="text-sm font-medium leading-tight">{c.label}</span>
                        </button>
                    );
                })}
            </div>
        </>
    );
}

function StepGoals({ goals, toggle }) {
    return (
        <>
            <StepHeader eyebrow="Your goals" title="What would change your year?" sub="We'll tune your results to match. Pick as many as you like." />
            <div className="mt-8 flex flex-wrap gap-2">
                {GOALS.map((g) => {
                    const active = goals.includes(g.id);
                    return (
                        <button key={g.id} onClick={() => toggle(g.id)} className={cn("px-4 py-2.5 rounded-full border text-sm transition-colors", active ? "bg-foreground text-background border-foreground" : "bg-card border-border hover:border-foreground/40")} data-testid={`onboarding-goal-${g.id}`}>
                            {active && <Check className="h-3.5 w-3.5 inline mr-1" />}
                            {g.label}
                        </button>
                    );
                })}
            </div>
        </>
    );
}
